import './DashboardJobCard.css';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import getSymbolFromCurrency from 'currency-symbol-map';
import get from 'lodash/get';
import { ReferralModal } from 'src/_shared/referral-modal';
import { formatJobLocation } from 'src/_shared/services/formatJobLocation.js';
import { ImInterestedButton } from 'src/_shared/components/im-interested-send-button';
import { ShareSendButton } from 'src/_shared/components/share-send-button';
import {
	calculateReferralBonus,
	formatBonusAmount,
	resolveActiveTieredBonus,
} from 'src/_shared/services/utils.js';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween.js';
dayjs.extend(isBetween);

const DashboardJobCard = memo(
	({
		job = {},
		currentUser,
		allMultiLingualData,
		contactIncentiveBonus,
		contacts = [],
		contactsNextToken,
		allowSelfReferrals,
		currentUserCompany,
		isExtendedUser,
		onUpdateJob,
		onCreateReferral,
		onCreateUserJobShare,
		onUpdateUserJobShare,
		userJobShares,
		setUpdatedContacts,
		navLocation,
		...otherProps
	}) => {
		const {
			id,
			title = '',
			department,
			isHotJob = false,
			campaign,
			tieredBonus,
			referralBonus,
			status,
			publicLink: jobPublicLink,
			internalJobLink,
			referrals = [],
			company,
			jobType,
			description = '',
			shares,
		} = job;

		const location = job?.job?.location || {};
		const userGroupCurrency = currentUser?.userGroup?.currency || 'USD';
		const disableReferrals = currentUser?.company?.disableReferrals ?? false;

		// Calculate bonus
		let isCampaignActive = false;
		if (!campaign?.archived && campaign?.startDate && campaign?.endDate) {
			const today = dayjs.utc();
			const startDate = dayjs.utc(campaign.startDate);
			const endDate = dayjs.utc(campaign.endDate);
			isCampaignActive = Boolean(
				today.isBetween(startDate, endDate, 'day', '[]')
			);
		}

		const currentTieredBonus = resolveActiveTieredBonus(
			job,
			currentUser?.userGroupId
		);

		const getBonusAmount = () => {
			const bonusValue = calculateReferralBonus(
				contactIncentiveBonus,
				referralBonus?.amount,
				currentUser?.incentiveEligible,
				currentTieredBonus,
				'employee',
				currentUser?.userGroupId
			);
			return formatBonusAmount(
				getSymbolFromCurrency(userGroupCurrency),
				bonusValue
			);
		};

		const referredBy = currentUser?.id;
		const languageCode =
			!currentUser?.languageCode || currentUser?.languageCode === 'US'
				? 'EN'
				: currentUser.languageCode;

		const { origin } = window.location;
		const jobUrl = `${origin}/newreferral/${referredBy}/${id}/${languageCode}`;
		const publicLink = jobUrl;

		const theme = JSON.parse(get(currentUser, 'company.theme', '{}'));

		return (
			<div className={`dashboard-job-card-simple${isHotJob ? ' hot-job' : ''}`}>
				<div className="dashboard-job-card-header">
					<Link
						to={{
							pathname: `/browsejobs/${id}`,
							state: {
								referrals,
								currentUserCompany,
							},
						}}
						className="dashboard-job-title"
					>
						{title}
					</Link>
					<div className="dashboard-job-icons">
						{isHotJob && <i className="icon-fire text-orange" />}
						{referralBonus?.hasBonus && !isExtendedUser && (
							<span className="dashboard-bonus-amount">{getBonusAmount()}</span>
						)}
					</div>
				</div>

				{department?.name && (
					<div className="dashboard-job-info-item">
						<i className="icon-building" />
						{department.name}
					</div>
				)}

				<div className="dashboard-job-location-row">
					<div className="dashboard-job-info-item">
						<i className="icon-location" />
						{formatJobLocation(location)}
					</div>
					{!currentUserCompany?.disableShareLink &&
						status === 'open' &&
						!isExtendedUser && (
							<div className="dashboard-share-button">
								<ShareSendButton
									iconOnly
									allMultiLingualData={allMultiLingualData}
									theme={theme}
									title={title}
									jobId={id}
									jobUrl={publicLink}
									company={company}
									jobType={jobType}
									description={description}
									city={location?.city || null}
									state={location?.state || null}
									shares={shares}
									referredBy={referredBy}
									languageCode={languageCode}
									currentUser={currentUser}
									userJobShares={userJobShares}
									onUpdateJob={onUpdateJob}
									onCreateUserJobShare={onCreateUserJobShare}
									onUpdateUserJobShare={onUpdateUserJobShare}
								/>
							</div>
						)}
				</div>

				{status === 'open' && !isExtendedUser && (
					<div className="dashboard-job-actions">
						<ReferralModal
							disabled={disableReferrals && !jobPublicLink}
							contacts={contacts}
							setUpdatedContacts={setUpdatedContacts}
							contactsNextToken={contactsNextToken}
							navLocation={navLocation}
							jobData={job}
							job={job}
							buttonType="dashboard"
							isBtnBlock="true"
							referrals={referrals}
							currentCurrencyRate={0}
							currentCurrencySymbol={'$'}
							contactIncentiveBonus={contactIncentiveBonus}
							campaignId={campaign?.id}
							test="test"
							onUpdateJob={onUpdateJob}
							onCreateUserJobShare={onCreateUserJobShare}
							onUpdateUserJobShare={onUpdateUserJobShare}
							allMultiLingualData={allMultiLingualData}
							currentUser={currentUser}
							{...otherProps}
						/>

						{allowSelfReferrals && (
							<ImInterestedButton
								key={job.id}
								allMultiLingualData={allMultiLingualData}
								contacts={contacts}
								currentUser={currentUser}
								internalJobLink={internalJobLink}
								job={job}
								onCreateReferral={onCreateReferral}
								buttonType="dashboard"
							/>
						)}
					</div>
				)}
			</div>
		);
	}
);

DashboardJobCard.displayName = 'DashboardJobCard';

export default DashboardJobCard;
